import React, { useState, useMemo, useEffect } from "react";
import "./style.scss";
import { FlagOutlined, EnvironmentFilled, EnvironmentOutlined } from "@ant-design/icons";
import { FormattedMessage } from "react-intl";
import { StartingPoint, EndPoint } from "@common-types";
import { IPickupDetails } from "@common-types";
import { Row, Col, Select, Collapse } from "antd";
import { cleanHtml } from "@share/utils";

const { Panel } = Collapse;

interface IProps {
  startingPoints?: StartingPoint[];
  pickupPoints?: IPickupDetails[];
  endPoints?: EndPoint[];
}

export const MeetingPickup: React.FC<IProps> = ({ startingPoints, pickupPoints, endPoints }) => {
  const filteredPickupPoints = useMemo(() => {
    return pickupPoints?.filter(point =>
      point.pickupType?.toLowerCase() !== 'other' &&
      point.description &&
      point.description.trim() !== ""
    ) || [];
  }, [pickupPoints]);

  const filteredStartingPoints = useMemo(() => {
    return startingPoints?.filter(point =>
      point.description &&
      point.description.trim() !== ""
    ) || [];
  }, [startingPoints]);

  const filteredEndPoints = useMemo(() => {
    return endPoints?.filter(point =>
      (point.description && point.description.trim() !== "") ||
      (point.address && point.address.trim() !== "")
    ) || [];
  }, [endPoints]);

  const [selectedPickupIndex, setSelectedPickupIndex] = useState(0);
  const [selectedMeetingIndex, setSelectedMeetingIndex] = useState(0);
  const [selectedEndPointIndex, setSelectedEndPointIndex] = useState(0);

  useEffect(() => {
    setSelectedPickupIndex(0);
  }, [filteredPickupPoints]);

  useEffect(() => {
    setSelectedMeetingIndex(0);
  }, [filteredStartingPoints]);

  useEffect(() => {
    setSelectedEndPointIndex(0);
  }, [filteredEndPoints]);

  const pickup = filteredPickupPoints?.[selectedPickupIndex];
  const meeting = filteredStartingPoints?.[selectedMeetingIndex];
  const end = filteredEndPoints?.[selectedEndPointIndex];

  const getMapUrl = (lat?: number, lng?: number) => {
    const latitude = Number(lat);
    const longitude = Number(lng);
    if (!latitude || !longitude) return "";
    return `https://www.google.com/maps?q=${latitude},${longitude}&output=embed`;
  };

  const formatAddress = (point?: any) => {
    if (!point) return "";
    const parts = [point.address, point.city, point.countryName, point.zip].filter(Boolean);
    return parts.join(", ");
  };

  if (!filteredPickupPoints.length && !filteredStartingPoints.length && !endPoints?.length) return null;

  const pickupMapUrl = getMapUrl(pickup?.latitude, pickup?.longitude);
  const meetingMapUrl = getMapUrl(meeting?.latitude, meeting?.longitude);
  const endMapUrl = getMapUrl(end?.latitude, end?.longitude);
  
  return (
    <div className="mp-wrapper">
      <Collapse
        defaultActiveKey={['1']}
        ghost
        expandIconPosition="end"
        className="mp-collapse"
      >
        <Panel
          header={
            <h2 className="mp-title">
              <FormattedMessage id="activity.details.meetingAndPickup" defaultMessage="Meeting and Pickup" />
            </h2>
          }
          key="1"
        >
          <div className="mp-main-card">
            <Row gutter={[48, 32]}>
              {/* Pickup Points Section */}
              {!!filteredPickupPoints?.length && (
                <Col xs={24} lg={12} className="mp-column">
                  <div className="section-header">
                    <div className="icon-wrapper">
                      <EnvironmentFilled className="section-icon" />
                    </div>
                    <span className="section-label">
                      <FormattedMessage id="activity.details.pickupPoints" defaultMessage="Pickup points" />
                    </span>
                  </div>

                  <div className="content-body">
                    {filteredPickupPoints.length > 1 ? (
                      <div className="select-container">
                        <div className="select-instruction">
                          <FormattedMessage id="activity.details.selectPickupPoint" defaultMessage="Select a pickup point" />
                        </div>
                        <Select
                          className="mp-select"
                          value={selectedPickupIndex}
                          onChange={setSelectedPickupIndex}
                          optionLabelProp="label"
                          dropdownClassName="mp-select-dropdown"
                        >
                          {filteredPickupPoints.map((point, index) => (
                            <Select.Option key={index} value={index} label={point.description}>
                              <div className="mp-option-item">
                                <div className="icon-box"><EnvironmentOutlined className="option-icon" /></div>
                                <div className="option-content">
                                  <div className="option-title">{point.description}</div>
                                  {point.address && <div className="option-address">{point.address}</div>}
                                </div>
                              </div>
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                    ) : (
                      pickup?.reference !== 'CONTACT_SUPPLIER_LATER' && pickup?.reference !== 'MEET_AT_DEPARTURE_POINT' ? (
                        <div className="point-details">
                          <div className="point-name">{pickup?.description}</div>
                          <div className="point-address">{formatAddress(pickup)}</div>
                        </div>) : null
                    )}

                    {pickup && (
                      <div className="action-area">
                        {pickupMapUrl ? (
                          <div className="small-map-frame">
                            <iframe src={pickupMapUrl} loading="lazy" allowFullScreen title="Pickup Point Map"></iframe>
                          </div>
                        ) : filteredPickupPoints.length <= 1 && pickup.description && (
                          <div
                            className="point-description-fallback"
                            dangerouslySetInnerHTML={{ __html: cleanHtml(pickup.description) }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </Col>
              )}

              {/* Meeting Points Section */}
              {!!filteredStartingPoints?.length && (
                <Col xs={24} lg={12} className="mp-column">
                  <div className="section-header">
                    <div className="icon-wrapper">
                      <EnvironmentFilled className="section-icon" />
                    </div>
                    <span className="section-label">
                      <FormattedMessage id="activity.details.meetingPoint" defaultMessage="Meeting point" />
                    </span>
                  </div>

                  <div className="content-body">
                    {filteredStartingPoints.length > 1 ? (
                      <div className="select-container">
                        <div className="select-instruction">
                          <FormattedMessage id="activity.details.selectMeetingPoint" defaultMessage="Select a meeting point" />
                        </div>
                        <Select
                          className="mp-select"
                          value={selectedMeetingIndex}
                          onChange={setSelectedMeetingIndex}
                          optionLabelProp="label"
                          dropdownClassName="mp-select-dropdown"
                        >
                          {filteredStartingPoints.map((point, index) => (
                            <Select.Option key={index} value={index} label={point.description}>
                              <div className="mp-option-item">
                                <div className="icon-box"><EnvironmentOutlined className="option-icon" /></div>
                                <div className="option-content">
                                  <div className="option-title">{point.description}</div>
                                  {point.address && <div className="option-address">{point.address}</div>}
                                </div>
                              </div>
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                    ) : (
                      meeting?.reference !== 'CONTACT_SUPPLIER_LATER' && meeting?.reference !== 'MEET_AT_DEPARTURE_POINT' ? (
                        <div className="point-details">
                          <div className="point-name">{meeting?.description}</div>
                          <div className="point-address">{formatAddress(meeting)}</div>
                        </div>) : null
                    )}

                    {meeting && (
                      <div className="action-area">
                        {meetingMapUrl ? (
                          <div className="small-map-frame">
                            <iframe src={meetingMapUrl} loading="lazy" allowFullScreen title="Meeting Point Map"></iframe>
                          </div>
                        ) : filteredStartingPoints.length <= 1 && meeting.description && (
                          <div
                            className="point-description-fallback"
                            dangerouslySetInnerHTML={{ __html: cleanHtml(meeting.description) }}
                          />
                        )}

                        {meeting.pickupInstructionsDescriptions?.length ? (
                          <div className="pickup-instructions" style={{ marginTop: '16px' }}>
                            {meeting.pickupInstructionsDescriptions.map((text, idx) => (
                              <p
                                key={idx}
                                className="instruction-text"
                                dangerouslySetInnerHTML={{ __html: cleanHtml(text) }}
                              />
                            ))}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                </Col>
              )}

              {/* End Point Section */}
              {(!endPoints || !!filteredEndPoints.length || (!filteredPickupPoints.length && !filteredStartingPoints.length)) && (
                <Col xs={24} lg={12} className="mp-column">
                  <div className="section-header">
                    <div className="icon-wrapper">
                      <FlagOutlined className="section-icon" />
                    </div>
                    <span className="section-label">
                      <FormattedMessage id="activity.details.endPoint" defaultMessage="End point" />
                    </span>
                  </div>

                  <div className="content-body">
                    {filteredEndPoints.length > 1 ? (
                      <div className="select-container">
                        <div className="select-instruction">
                          <FormattedMessage id="activity.details.selectEndPoint" defaultMessage="Select an end point" />
                        </div>
                        <Select
                          className="mp-select"
                          value={selectedEndPointIndex}
                          onChange={setSelectedEndPointIndex}
                          optionLabelProp="label"
                          dropdownClassName="mp-select-dropdown"
                        >
                          {filteredEndPoints.map((point, index) => (
                            <Select.Option key={index} value={index} label={point.description || point.address}>
                              <div className="mp-option-item">
                                <div className="icon-box"><EnvironmentOutlined className="option-icon" /></div>
                                <div className="option-content">
                                  <div className="option-title">{point.description || point.address}</div>
                                  {point.description && point.address && <div className="option-address">{point.address}</div>}
                                </div>
                              </div>
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                    ) : filteredEndPoints.length === 1 ? (
                      <div className="point-details">
                        <div className="point-name">{end?.description || end?.address}</div>
                        <div className="point-address">{formatAddress(end)}</div>
                      </div>
                    ) : (
                      <div className="point-details">
                        <div className="point-name">
                          <FormattedMessage id="activity.details.sameAsStarting" defaultMessage="Same as Starting Point" />
                        </div>
                        <div className="point-address">
                          <FormattedMessage id="activity.details.loopTour" defaultMessage="This activity ends back at the meeting point." />
                        </div>
                      </div>
                    )}

                    {end && (
                      <div className="action-area">
                        {endMapUrl && (
                          <div className="small-map-frame">
                            <iframe src={endMapUrl} loading="lazy" allowFullScreen title="End Point Map"></iframe>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Col>
              )}
            </Row>
          </div>
        </Panel>
      </Collapse>
    </div>
  );
};
